import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, interval, Subscription, of, throwError } from 'rxjs';
import { map, switchMap, catchError, tap } from 'rxjs/operators';
import jwt_decode from 'jwt-decode';
import { environment } from '../../environments/environment';
import { User } from '@app/_models';
import { FingerprintService, DeviceFingerprint } from './fingerprint.service';

const httpOptions = {
  headers: new HttpHeaders({
    key: 'rejc41r2421sk8423jd39',
  }),
};

export interface VerificationMethods {
  email: boolean;
  totp: boolean;
  user_email: string;
}

export interface VerificationResponse {
  requires_verification: boolean;
  temp_token: string;
  verification_methods: VerificationMethods;
  device_type: 'desktop' | 'mobile';
  replacing_device: string | null;
  message: string;
}

export interface TwoFactorSetupResponse {
  setup_token: string;
  secret: string;
  qr_code: string;
}

export interface TrustedDevice {
  id: number;
  device_name: string;
  ip_address: string;
  created_at: string;
  last_used_at: string;
  expires_at: string;
}

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private currentUserSubject: BehaviorSubject<User>;
  private currentUserData: BehaviorSubject<User>;
  public currentUser: Observable<User>;
  private heartbeatSubscription?: Subscription;
  private pendingVerificationSubject = new BehaviorSubject<boolean>(false);
  public pendingVerification$ = this.pendingVerificationSubject.asObservable();
  private tempToken: string | null = null;
  private pendingFingerprint: DeviceFingerprint | null = null;
  private verificationMethods: VerificationMethods | null = null;
  private deviceType: 'desktop' | 'mobile' | null = null;
  private replacingDevice: string | null = null;


  private publicRoutes = [
    '/login',
    '/register',
    '/activate-account',
    '/password-reset',
    '/forgot-password'
  ];

  apiUrl = `${environment.api_backend.url}`;

  constructor(
    private http: HttpClient,
    private router: Router,
    private fingerprintService: FingerprintService
  ) {
    try {
      this.currentUserSubject = new BehaviorSubject<User>(
        JSON.parse(localStorage.getItem('currentUser'))
      );
      this.currentUserData = new BehaviorSubject<User>(
        JSON.parse(localStorage.getItem('currentUserData'))
      );
      this.currentUser = this.currentUserSubject.asObservable();

      // Start heartbeat if user is already authenticated
      if (this.currentUserValue && !this.isPublicRoute(this.router.url)) {
        this.startHeartbeat();
      }
    } catch (err) {
      console.log('logout');
      this.logout();
    }
  }

  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  public get currentUserInfo(): User {
    return JSON.parse(localStorage.getItem('currentUserData'));
  }

  private isPublicRoute(url: string): boolean {
    return this.publicRoutes.some(route => url.includes(route));
  }

  login(username: string, password: string): Observable<any> {
    return new Observable(observer => {
      console.log('Collecting fingerprint...');

      // Add 5 second timeout for fingerprint collection
      const fingerprintPromise = Promise.race([
        this.fingerprintService.getFingerprint(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Fingerprint timeout')), 5000)
        )
      ]);

      fingerprintPromise.then((fingerprint: DeviceFingerprint) => {
        console.log('Fingerprint collected, making request...');

        this.http
          .post<any>(this.apiUrl + `/users/authenticate`, {
            username,
            password,
            fingerprint: fingerprint.hash,
            fingerprintComponents: fingerprint.components
          })
          .subscribe({
            next: (response) => {
              console.log('Auth response received:', response);

              if (response.requires_verification) {
                console.log('Setting up verification state...');
                this.tempToken = response.temp_token;
                this.pendingFingerprint = fingerprint;
                this.verificationMethods = response.verification_methods;
                this.deviceType = response.device_type;
                this.replacingDevice = response.replacing_device;
                this.pendingVerificationSubject.next(true);

                observer.next({
                  requires_verification: true,
                  verification_methods: response.verification_methods,
                  device_type: response.device_type,
                  replacing_device: response.replacing_device,
                  message: response.message
                });
                observer.complete();
              } else {
                console.log('Handling successful login...');
                this.handleSuccessfulLogin(response);
                observer.next(response);
                observer.complete();
              }
            },
            error: (error) => {
              console.log('HTTP error:', error);
              observer.error(error);
            }
          });
      }).catch(error => {
        console.warn('Fingerprint failed or timeout, continuing without:', error);

        // Continue login without fingerprint
        this.http
          .post<any>(this.apiUrl + `/users/authenticate`, { username, password })
          .subscribe({
            next: (response) => {
              console.log('Auth response (no fingerprint):', response);
              if (response.requires_verification) {
                this.tempToken = response.temp_token;
                this.verificationMethods = response.verification_methods;
                this.deviceType = response.device_type;
                this.replacingDevice = response.replacing_device;
                this.pendingVerificationSubject.next(true);
                observer.next({
                  requires_verification: true,
                  verification_methods: response.verification_methods,
                  device_type: response.device_type,
                  replacing_device: response.replacing_device
                });
              } else {
                this.handleSuccessfulLogin(response);
                observer.next(response);
              }
              observer.complete();
            },
            error: (err) => {
              console.log('HTTP error (no fingerprint):', err);
              observer.error(err);
            }
          });
      });
    });
  }

  // Handle successful login (extracted for reuse)
  private handleSuccessfulLogin(user: any): void {
    localStorage.setItem('currentUser', JSON.stringify(user));
    localStorage.setItem('currentUserData', JSON.stringify(jwt_decode(user.token)));
    this.currentUserSubject.next(user);
    this.pendingVerificationSubject.next(false);
    this.tempToken = null;
    this.pendingFingerprint = null;
    this.verificationMethods = null;
    this.deviceType = null;
    this.replacingDevice = null;
    this.startHeartbeat();
  }

  // =====================================================
  // Verification for new device
  // =====================================================

  // Verify with email code or TOTP
  verifyDevice(code: string, trustDevice: boolean = true): Observable<any> {
    if (!this.tempToken) {
      return throwError(() => 'No pending verification');
    }

    return this.http
      .post<any>(this.apiUrl + `/users/verify-2fa`, {
        temp_token: this.tempToken,
        code: code,
        trust_device: trustDevice
      })
      .pipe(
        tap(response => {
          // ONLY handle successful login if NEITHER 2FA setup NOR TOTP is required
          // If requires_2fa_setup or requires_totp is true, we're still in the flow
          if (!response.requires_2fa_setup && !response.requires_totp) {
            this.handleSuccessfulLogin(response);
          }
        }),
        catchError(error => {
          console.error('verifyDevice error:', error);
          const errorMessage = error?.error?.error || error?.error?.message || 'Cod invalid';
          return throwError(() => errorMessage);
        })
      );
  }

  // Complete forced 2FA setup after email verification
  complete2FASetupFlow(setupFlowToken: string, code: string): Observable<any> {
    return this.http
      .post<any>(this.apiUrl + `/users/2fa/complete-setup`, {
        setup_flow_token: setupFlowToken,
        code: code
      })
      .pipe(
        tap(response => {
          // Don't call handleSuccessfulLogin here - component will handle it after showing backup codes
          // The component will call continueToApp() which uses window.location.href
          // But we need to store the user data for the redirect to work
          localStorage.setItem('currentUser', JSON.stringify(response));
          localStorage.setItem('currentUserData', JSON.stringify(jwt_decode(response.token)));
        }),
        catchError(error => {
          const errorMessage = error?.error?.error || error?.error?.message || 'Setup failed';
          return throwError(() => errorMessage);
        })
      );
  }

  // Resend email verification code
  resendVerificationCode(): Observable<any> {
    if (!this.tempToken) {
      return throwError(() => new Error('No pending verification'));
    }

    return this.http.post<any>(this.apiUrl + `/users/verify-2fa/resend`, {
      temp_token: this.tempToken
    });
  }

  // Cancel pending verification
  cancelVerification(): void {
    this.pendingVerificationSubject.next(false);
    this.tempToken = null;
    this.pendingFingerprint = null;
    this.verificationMethods = null;
    this.deviceType = null;
    this.replacingDevice = null;
  }

  // Check if verification is pending
  isVerificationPending(): boolean {
    return this.pendingVerificationSubject.value;
  }

  // Get current verification methods
  getVerificationMethods(): VerificationMethods | null {
    return this.verificationMethods;
  }

  // Get device type being verified
  getDeviceType(): 'desktop' | 'mobile' | null {
    return this.deviceType;
  }

  // Get device being replaced (if any)
  getReplacingDevice(): string | null {
    return this.replacingDevice;
  }

  // =====================================================
  // TOTP Setup (Optional - for enhanced security)
  // =====================================================

  // Initialize TOTP setup
  setup2FA(): Observable<TwoFactorSetupResponse> {
    return this.http.post<TwoFactorSetupResponse>(
      this.apiUrl + `/users/2fa/setup`,
      {},
      httpOptions
    );
  }

  // Complete TOTP setup by verifying first code
  verify2FASetup(setupToken: string, code: string): Observable<any> {
    return this.http.post<any>(
      this.apiUrl + `/users/2fa/setup/verify`,
      { setup_token: setupToken, code: code },
      httpOptions
    );
  }

  // Disable TOTP
  disable2FA(password: string): Observable<any> {
    return this.http.post<any>(
      this.apiUrl + `/users/2fa/disable`,
      { password: password },
      httpOptions
    );
  }

  // Get 2FA status
  get2FAStatus(): Observable<any> {
    return this.http.get<any>(
      this.apiUrl + `/users/2fa/status`,
      httpOptions
    );
  }

  // =====================================================
  // Device Management
  // =====================================================

  // Get trusted devices
  getTrustedDevices(): Observable<{ devices: TrustedDevice[] }> {
    return this.http.get<{ devices: TrustedDevice[] }>(
      this.apiUrl + `/users/devices`,
      httpOptions
    );
  }

  // Revoke a specific device
  revokeDevice(deviceId: number): Observable<any> {
    return this.http.delete<any>(
      this.apiUrl + `/users/devices?device_id=${deviceId}`,
      httpOptions
    );
  }

  // Revoke all devices
  revokeAllDevices(keepCurrent: boolean = true): Observable<any> {
    return new Observable(observer => {
      this.fingerprintService.getFingerprint().then(fingerprint => {
        console.log('Fingerprint hash:', fingerprint.hash);
        console.log('Fingerprint components:', fingerprint.components);

        this.http.post<any>(
          this.apiUrl + `/users/devices/revoke-all`,
          {
            keep_current: keepCurrent,
            current_fingerprint: keepCurrent ? fingerprint.hash : null
          },
          httpOptions
        ).subscribe({
          next: (response) => {
            observer.next(response);
            observer.complete();
          },
          error: (error) => observer.error(error)
        });
      });
    });
  }


  logout(callApi: boolean = true) {
    // Stop heartbeat
    this.stopHeartbeat();

    this.fingerprintService.clearCache();

    // Clear verification state
    this.pendingVerificationSubject.next(false);
    this.tempToken = null;
    this.pendingFingerprint = null;
    this.verificationMethods = null;


    // Call logout API if needed
    if (callApi) {
      this.http.post(this.apiUrl + '/users/logout', {}, httpOptions)
        .subscribe({
          error: (error) => console.error('Logout API error:', error)
        });
    }

    // remove user from local storage to log user out
    localStorage.removeItem('currentUser');
    localStorage.removeItem('currentUserData');
    this.currentUserSubject.next(null);
    this.currentUserData.next(null);

    // Navigate to login
    if (!this.isPublicRoute(this.router.url)) {
      this.router.navigate(['/login']);
    }
  }


  register(currentUser: User) {
    return this.http.post(this.apiUrl + '/users', currentUser, httpOptions);
  }

  passwordreset(currentUser: User) {
    return this.http.post(
      this.apiUrl + '/password_reset',
      currentUser,
      httpOptions
    );
  }

  // Heartbeat methods
  startHeartbeat(): void {
    // Check if heartbeat is enabled in environment
    if (!environment.enableHeartbeat) {
      return;
    }

    if (this.isPublicRoute(this.router.url)) {
      return;
    }

    // Don't start if user is not authenticated
    if (!this.currentUserValue) {
      return;
    }

    if (this.currentUserInfo.role == 'admin') {
      return;
    }
    // Stop any existing heartbeat
    this.stopHeartbeat();


    // Start heartbeat interval
    this.heartbeatSubscription = interval(environment.heartbeatInterval || 15000)
      .pipe(
        switchMap(() => this.checkSession())
      )
      .subscribe({
        next: (response) => {
          console.log('Heartbeat: Session active');
        },
        error: (error) => {
          if (error.status === 401) {
            this.handleSessionInvalidated(error);
          }
        }
      });
  }

  stopHeartbeat(): void {
    if (this.heartbeatSubscription) {
      this.heartbeatSubscription.unsubscribe();
      this.heartbeatSubscription = undefined;
    }
  }

  private checkSession(): Observable<any> {
    return this.http.get(this.apiUrl + '/users/heartbeat', httpOptions);
  }

  private handleSessionInvalidated(error: any): void {
    this.stopHeartbeat();

    const message = error.error?.code === 'SESSION_INVALIDATED'
      ? 'Sesiunea dumneavoastră a fost invalidată deoarece v-ați autentificat de pe alt dispozitiv.'
      : 'Sesiunea dumneavoastră a expirat. Vă rugăm să vă autentificați din nou.';

    alert(message);
    this.logout(false); // false = don't call API since session is already invalid
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!this.currentUserValue;
  }
}
